import { useEffect, useState } from 'react'
import type { AnswerMap, PhaseInfo, WorkflowStage } from '../../types/workflow'
import { API_BASE_URL } from '@/lib/apiConfig';

interface ReviewSubmitTemplateProps {
  stage: WorkflowStage
  answers: AnswerMap
  currentPhase?: PhaseInfo | null
  onContinue: () => void
  onSubmit?: () => Promise<void>
}

const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL ||
  API_BASE_URL
).replace(/\/+$/, '')

const NEXT_STEPS = [
  'A licensed clinician will review your assessment',
  "You'll receive an email with next steps within 24 hours",
  'If approved, your prescription will be sent to the pharmacy',
  'Our team may contact you if additional information is needed',
]

export default function ReviewSubmitTemplate({
  answers,
  onContinue,
  onSubmit,
}: ReviewSubmitTemplateProps) {
  const firstName = (answers.first_name as string) || ''
  const intakeToken = answers.intake_token as string | undefined

  // Check payment status and whether the clinic is guided
  const [paymentAlreadyOnFile, setPaymentAlreadyOnFile] = useState(false)
  const [isGuided, setIsGuided] = useState<boolean | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!intakeToken) return

    const checkIntakeStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/intakes/validate/${intakeToken}`)
        const data = await response.json()
        if (response.ok && data?.success) {
          const status = data.data.paymentStatus
          setPaymentAlreadyOnFile(status === 'hold_placed' || status === 'captured')
          setIsGuided(data.data.isGuided ?? false)
        } else {
          setIsGuided(false)
        }
      } catch {
        // Default to non-guided (no payment step)
        setIsGuided(false)
      }
    }

    void checkIntakeStatus()
  }, [intakeToken])

  const handleButtonClick = async () => {
    if (!isGuided && onSubmit) {
      setIsSubmitting(true)
      setSubmitError(null)
      try {
        await onSubmit()
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Submission failed. Please try again.')
      } finally {
        setIsSubmitting(false)
      }
    } else {
      onContinue()
    }
  }

  // Determine if payment step should be shown
  const showPaymentNotice = isGuided === true

  // Determine button text
  let buttonText = 'Submit My Assessment'
  if (showPaymentNotice) {
    buttonText = paymentAlreadyOnFile ? 'Continue to Submit' : 'Continue to Payment'
  }

  return (
    <div className="flex-1 flex flex-col gap-5">

      {/* Animated checkmark hero */}
      <div className="flex flex-col items-center py-6 gap-3">
        <div className="relative w-[88px] h-[88px]">
          <svg
            viewBox="0 0 88 88"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full"
          >
            <circle
              cx="44"
              cy="44"
              r="42"
              stroke="var(--ww-accent)"
              strokeWidth="2"
              fill="none"
              strokeOpacity="0.25"
            />
            <circle
              cx="44"
              cy="44"
              r="42"
              stroke="var(--ww-accent)"
              strokeWidth="2"
              fill="none"
              strokeDasharray="264"
              strokeDashoffset="0"
              className="review-circle-draw"
            />
            <path
              d="M28 44 L40 56 L62 34"
              stroke="var(--ww-accent)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="52"
              strokeDashoffset="0"
              className="review-check-draw"
            />
          </svg>
        </div>
        {firstName && (
          <p className="text-[15px] font-medium text-[var(--ww-text-2)] text-center">
            Great work{firstName ? `, ${firstName}` : ''}!
          </p>
        )}
      </div>

      {/* What happens next card */}
      <div className="rounded-2xl border border-[var(--ww-glass-border)] bg-[var(--ww-surface)] glass overflow-hidden">
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-[var(--ww-glass-border)]">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[rgba(74,138,154,0.12)] border border-[var(--ww-accent)]/20">
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-[var(--ww-accent)]" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="12" cy="12" r="10" />
            </svg>
          </div>
          <h3 className="text-[15px] font-semibold text-[var(--ww-text)]">What happens next</h3>
        </div>
        <ul className="px-5 py-4 space-y-4">
          {NEXT_STEPS.map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-[5px] flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[rgba(74,138,154,0.12)] border border-[var(--ww-accent)]/20 text-[10px] font-bold text-[var(--ww-accent)]">
                {i + 1}
              </span>
              <span className="text-[14px] leading-relaxed text-[var(--ww-text-2)]">{step}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Trust badge */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--ww-glass-border)] bg-[var(--ww-surface)] glass">
        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 flex-shrink-0 text-[var(--ww-accent)]" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p className="text-[13px] text-[var(--ww-text-2)] leading-snug">
          Your information is <span className="font-semibold text-[var(--ww-text)]">encrypted and HIPAA-compliant</span>. We never share your data without consent.
        </p>
      </div>

      {/* Payment step notice — only shown for guided clinics */}
      {showPaymentNotice && (
        paymentAlreadyOnFile ? (
          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--ww-accent)]/20 bg-[rgba(74,138,154,0.05)]">
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 flex-shrink-0 text-[var(--ww-accent)]" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="text-[13px] text-[var(--ww-text-2)] leading-snug">
              Payment is already on file. <span className="font-semibold text-[var(--ww-text)]">You won't be charged again.</span>
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--ww-accent)]/20 bg-[rgba(74,138,154,0.05)]">
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 flex-shrink-0 text-[var(--ww-accent)]" stroke="currentColor" strokeWidth="1.5">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
            <p className="text-[13px] text-[var(--ww-text-2)] leading-snug">
              Next: <span className="font-semibold text-[var(--ww-text)]">Securely add your payment method</span>
            </p>
          </div>
        )
      )}

      {submitError && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-red-200 bg-red-50 text-[13px] text-red-700">
          <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 flex-shrink-0 text-red-500" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" strokeLinecap="round" />
            <line x1="12" y1="16" x2="12.01" y2="16" strokeLinecap="round" />
          </svg>
          <p>{submitError}</p>
        </div>
      )}

      <div className="mt-auto pt-4 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          type="button"
          onClick={handleButtonClick}
          disabled={isGuided === null || isSubmitting}
          className="flex w-full py-4 px-6 items-center justify-center gap-2 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
        >
          {isSubmitting ? 'Submitting...' : buttonText}
        </button>
      </div>
    </div>
  )
}
